// MemberMonReseau.jsx — Tableau de bord personnel networking (Phase B)
// Stats : vues, recos reçues, clics, messages, mises en relation
// + mes favoris, mes recos reçues, mes mises en relation
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import {
  Eye, Award, Heart, UserPlus, MessageCircle, Mail, Phone, Globe,
  Loader2, Linkedin, Instagram, Facebook, MapPin, Check, X as XIcon, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

export default function MemberMonReseau() {
  const [stats, setStats] = useState(null);
  const [favoris, setFavoris] = useState([]);
  const [recos, setRecos] = useState([]);
  const [mrRecues, setMrRecues] = useState([]);
  const [mrEnvoyees, setMrEnvoyees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('stats');

  const load = async () => {
    setLoading(true);
    try {
      const [s, f, r, mrR, mrE] = await Promise.all([
        api.get('/networking/me/stats'),
        api.get('/networking/favoris/mine').catch(() => ({ data: [] })),
        api.get('/networking/me/recommandations-recues').catch(() => ({ data: [] })),
        api.get('/networking/mises-en-relation/recues').catch(() => ({ data: [] })),
        api.get('/networking/mises-en-relation/envoyees').catch(() => ({ data: [] })),
      ]);
      setStats(s.data); setFavoris(f.data); setRecos(r.data); setMrRecues(mrR.data); setMrEnvoyees(mrE.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateMR = async (id, statut) => {
    try {
      await api.patch(`/networking/mises-en-relation/${id}`, { statut });
      toast.success(statut === 'acceptee' ? 'Demande acceptée' : 'Demande refusée');
      load();
    } catch { toast.error('Erreur'); }
  };

  if (loading || !stats) {
    return <div className="p-8 grid place-items-center min-h-[60vh]"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="member-mon-reseau">
      {/* En-tête éditorial */}
      <div className="mb-10 slide-up">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--theme-primary)' }}>Mon réseau</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0F172A] leading-[1.02]">
          Vos <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>interactions.</span>
        </h1>
        <p className="text-base text-[#475569] mt-3">Suivez l'impact de votre fiche et vos relations dans le réseau.</p>
      </div>

      {!stats.has_fiche && (
        <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] rounded-r-xl p-5 mb-8 flex items-start gap-3">
          <UserPlus className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
          <div className="text-sm text-[#78350F] leading-relaxed">
            <strong className="text-[#0F172A]">Créez votre fiche entreprise</strong> pour apparaître dans l'annuaire et commencer à recevoir des recommandations et des mises en relation.{' '}
            <Link to="/espace/annuaire" className="underline font-bold">Aller à l'annuaire →</Link>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8 stagger" data-testid="reseau-kpis">
        <Stat icon={Eye} label="Vues de ma fiche" value={stats.vues_total || 0} sub={`${stats.vues} uniques`} testid="stat-vues" />
        <Stat icon={Award} label="Recommandations" value={stats.recommandations} sub="reçues" testid="stat-recos" />
        <Stat icon={UserPlus} label="Mises en relation" value={stats.mises_en_relation} sub="reçues" testid="stat-mr" />
        <Stat icon={MessageCircle} label="Clics contact" value={stats.clics_total} sub="email/tél/web" testid="stat-clics" />
      </div>

      {/* Détail clics par type */}
      {stats.clics_total > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-6 mb-8" data-testid="clics-detail">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-3">Détail des clics sur ma fiche</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { key: 'email', icon: Mail, label: 'Email' },
              { key: 'telephone', icon: Phone, label: 'Téléphone' },
              { key: 'site_web', icon: Globe, label: 'Site web' },
              { key: 'linkedin', icon: Linkedin, label: 'LinkedIn' },
              { key: 'instagram', icon: Instagram, label: 'Instagram' },
              { key: 'facebook', icon: Facebook, label: 'Facebook' },
            ].map(({ key, icon: Ic, label }) => (
              <div key={key} className="p-3 rounded-xl bg-[#F9F9F8] text-center">
                <Ic className="w-4 h-4 mx-auto mb-1.5 text-[#94A3B8]" />
                <div className="font-display text-xl font-black tracking-tight text-[#0F172A]">{stats.clics_par_type[key] || 0}</div>
                <div className="text-[10px] text-[#475569] mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-[#E2E8F0] mb-6 overflow-x-auto" data-testid="reseau-tabs">
        <TabBtn active={tab === 'favoris'} onClick={() => setTab('favoris')} count={favoris.length} testid="tab-favoris">Mes favoris</TabBtn>
        <TabBtn active={tab === 'recos'} onClick={() => setTab('recos')} count={recos.length} testid="tab-recos">Recos reçues</TabBtn>
        <TabBtn active={tab === 'mr-recues'} onClick={() => setTab('mr-recues')} count={mrRecues.filter((m) => m.statut === 'en_attente').length} testid="tab-mr-recues">Mises en relation reçues</TabBtn>
        <TabBtn active={tab === 'mr-envoyees'} onClick={() => setTab('mr-envoyees')} count={mrEnvoyees.length} testid="tab-mr-envoyees">Envoyées</TabBtn>
      </div>

      {tab === 'favoris' && (
        favoris.length === 0 ? <Empty icon={Heart} title="Aucun favori" desc="Parcourez l'annuaire et ajoutez vos contacts préférés." />
        : <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="favoris-list">
            {favoris.map((f) => (
              <div key={f.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-4 flex items-center gap-3">
                {f.fiche.logo_url ? <img src={f.fiche.logo_url} className="w-10 h-10 rounded-lg object-contain bg-[#F1F5F9]" alt="" /> : <div className="w-10 h-10 rounded-lg grid place-items-center font-display font-black text-xs" style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}>{f.fiche.nom_entreprise?.[0]}</div>}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0F172A] truncate">{f.fiche.nom_entreprise}</div>
                  <div className="text-xs text-[#475569] truncate">{f.fiche.metier} · {f.fiche.ville}</div>
                </div>
                <Link to="/espace/annuaire" className="text-xs font-bold" style={{ color: 'var(--theme-primary)' }}>Voir →</Link>
              </div>
            ))}
          </div>
      )}

      {tab === 'recos' && (
        recos.length === 0 ? <Empty icon={Award} title="Aucune recommandation reçue" desc="Encouragez les membres qui vous connaissent à recommander votre fiche." />
        : <div className="space-y-3" data-testid="recos-list">
            {recos.map((r) => (
              <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-[#0F172A]">{r.from_prenom} {r.from_nom}</div>
                  <div className="text-xs text-[#94A3B8]">{new Date(r.created_at).toLocaleDateString('fr-FR')}</div>
                </div>
                <p className="text-sm text-[#475569] leading-relaxed whitespace-pre-wrap">{r.texte}</p>
              </div>
            ))}
          </div>
      )}

      {tab === 'mr-recues' && (
        mrRecues.length === 0 ? <Empty icon={UserPlus} title="Aucune demande" desc="Quand un membre voudra entrer en contact via votre fiche, vous le verrez ici." />
        : <div className="space-y-3" data-testid="mr-recues-list">
            {mrRecues.map((m) => <MRCard key={m.id} mr={m} onAccept={() => updateMR(m.id, 'acceptee')} onRefuse={() => updateMR(m.id, 'refusee')} mode="recue" />)}
          </div>
      )}

      {tab === 'mr-envoyees' && (
        mrEnvoyees.length === 0 ? <Empty icon={UserPlus} title="Aucune demande envoyée" desc="Parcourez l'annuaire pour demander une mise en relation." />
        : <div className="space-y-3" data-testid="mr-envoyees-list">
            {mrEnvoyees.map((m) => <MRCard key={m.id} mr={m} mode="envoyee" />)}
          </div>
      )}
    </div>
  );
}

function Stat({ icon: Ic, value, label, sub, testid }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-3xl p-4 sm:p-5 transition-all hover:shadow-md hover:-translate-y-0.5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] truncate">{label}</div>
        <Ic className="w-4 h-4 shrink-0 ml-2" style={{ color: 'var(--theme-primary)' }} />
      </div>
      <div className="font-display text-3xl font-black tracking-tight text-[#0F172A]">{value}</div>
      <div className="text-xs text-[#94A3B8] mt-1">{sub}</div>
    </div>
  );
}

function TabBtn({ active, onClick, count, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`relative px-4 py-3 text-sm font-semibold whitespace-nowrap transition-colors ${active ? 'text-[#0F172A]' : 'text-[#94A3B8] hover:text-[#475569]'}`}>
      {children}
      {count > 0 && <span className={`ml-1.5 text-[10px] font-black px-1.5 py-0.5 rounded-full ${active ? 'bg-[var(--theme-primary)] text-white' : 'bg-[#F1F5F9] text-[#475569]'}`}>{count}</span>}
      {active && <span className="absolute -bottom-px inset-x-0 h-0.5 bg-[var(--theme-primary)]" />}
    </button>
  );
}

function Empty({ icon: Ic, title, desc }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-3xl p-12 text-center">
      <Ic className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
      <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2">{title}</h3>
      <p className="text-sm text-[#475569]">{desc}</p>
    </div>
  );
}

function MRCard({ mr, onAccept, onRefuse, mode }) {
  const statutBadge = mr.statut === 'acceptee' ? <span className="badge badge-success"><Check className="w-2.5 h-2.5" /> Acceptée</span>
                     : mr.statut === 'refusee' ? <span className="badge badge-danger">Refusée</span>
                     : <span className="badge badge-info"><Clock className="w-2.5 h-2.5" /> En attente</span>;
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid={`mr-card-${mr.id}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="font-semibold text-[#0F172A]">
          {mode === 'recue' ? <>De : {mr.from_prenom} {mr.from_nom}</> : <>À : {mr.to_nom_entreprise}</>}
        </div>
        <div className="flex items-center gap-2">
          <span className="badge badge-neutral capitalize">{mr.contexte}</span>
          {statutBadge}
        </div>
      </div>
      <p className="text-sm text-[#475569] leading-relaxed whitespace-pre-wrap mb-3">{mr.message}</p>
      <div className="text-xs text-[#94A3B8]">{new Date(mr.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
      {mode === 'recue' && mr.statut === 'en_attente' && (
        <div className="flex gap-2 mt-4 pt-4 border-t border-[#F1F5F9]">
          <button onClick={onAccept} className="btn-primary !text-xs" data-testid={`mr-accept-${mr.id}`}><Check className="w-3 h-3" /> Accepter</button>
          <button onClick={onRefuse} className="btn-secondary !text-xs" data-testid={`mr-refuse-${mr.id}`}><XIcon className="w-3 h-3" /> Refuser</button>
          {mr.from_email && <a href={`mailto:${mr.from_email}`} className="btn-ghost !text-xs ml-auto"><Mail className="w-3 h-3" /> {mr.from_email}</a>}
        </div>
      )}
    </div>
  );
}
